// CandleFlame
// Features:
// - Procedural flame shader using Perlin noise
// - Animated visibility transition when toggled

import { useEffect, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'
import gsap from 'gsap'

import { useStore } from '../../store/useStore'
import vertexShader from '../shaders/candleFlame/vertex.glsl'
import fragmentShader from '../shaders/candleFlame/fragment.glsl'

import perlinTextureURL from '/textures/perlin.png'

export default function CandleFlame({
    position = [0, 0, 0],
    radiusTop = 0.005,
    radiusBottom = 0.015,
    height = 0.12,
    textureScale = 0.2,
    opacity = 1.0,
}) {
    const candleOn = useStore((state) => state.candleOn)

    // Textures
    const perlinTexture = useTexture(perlinTextureURL, (t) => {
        t.colorSpace = THREE.NoColorSpace
        t.wrapS = THREE.RepeatWrapping
        t.wrapT = THREE.RepeatWrapping
        t.minFilter = THREE.LinearFilter
        t.magFilter = THREE.LinearFilter
    })

    // Geometry: Open-ended cylinder for the flame
    const geometry = useMemo(() => {
        return new THREE.CylinderGeometry(
            radiusTop,
            radiusBottom,
            height,
            32,
            3,
            true // openEnded
        )
    }, [radiusTop, radiusBottom, height])

    // Material: Shader material for the flame effect
    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uPerlinTexture: { value: perlinTexture },
                uOpacity: { value: opacity },
                uTextureScale: { value: textureScale },
                uHeight: { value: height },
                uColorBottom: { value: new THREE.Color('#ff2a00') },
                uColorTop: { value: new THREE.Color('#ffd400') },
                alphaParam: { value: 0.0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
        })
    }, [perlinTexture, opacity, textureScale, height])

    // Animation: Toggle visibility
    useEffect(() => {
        if (!material) return
        gsap.to(material.uniforms.alphaParam, {
            value: candleOn ? 1.0 : 0.0,
            duration: 0.8,
            ease: 'power2.inOut',
            overwrite: 'auto',
        })
    }, [candleOn, material])

    // Loop: Update time uniform
    useFrame((state) => {
        if (material) {
            material.uniforms.uTime.value = state.clock.elapsedTime
            material.uniforms.uHeight.value = height
        }
    })

    return <mesh position={position} geometry={geometry} material={material} />
}
